import EntityImage from './EntityImage';
import EditorMarker from './EditorMarker';
import BoundsMarker from './editor/BoundsMarker';
import ChunkBoundaries from '../components/ChunkBoundaries';
import PlatformImage from './PlatformImageBase';
import { PlatformFactory } from './PlatformFactory';
import { TrapFactory } from './TrapFactory';
import { EntityFactory } from './EntityFactory';
import SmallGemMarker from './editor/entity-markers/SmallGemMarker';
import EntityContainer from './EntityContainer';
import EntitySmallGem from './entities/EntitySmallGem';
import SmashableMarkerCrate from './editor/entity-markers/SmashableMarkerCrate';
import SmashableMarkerVine from './editor/entity-markers/SmashableMarkerVine';
import SmashableMarkerRocks from './editor/entity-markers/SmashableMarkerRocks';
import SimpleBreakableBlocker from './../components/platformer/SimpleBreakableBlocker';
import FinishMarker from './editor/FinishMarker';
import EditorPlatformMarkerImage from './EditorPlatformMarkerImage';
import PlatformMarkerFoliage3 from './editor/platform-markers/jungle/PlatformMarkerFoliage3';
import SimpleMover from './../components/platformer/SimpleMover';
import DecorationImage from './decorations/DecorationImage';
import DecorationHelpSwitchDesk from './decorations/shipwreck/DecorationHelpSwitchDesk';
import DecorationHelpJumpDesk from './decorations/shipwreck/DecorationHelpJumpDesk';
import EditorTrapMarker from './EditorTrapMarker';
import TrapImage from './TrapImageBase';
import TrapMarkerSpikes from './editor/trap-markers/jungle/TrapMarkerSpikes';
import TrapMarkerCoconuts from './editor/trap-markers/jungle/TrapMarkerCoconuts';
import TrapMarkerTorch from './editor/trap-markers/cave/TrapMarkerTorch';
import EntitySmashable from './EntitySmashable';
import EditorPlatformMarker from './EditorPlatformMarker';
import PlatformContainer from './PlatformContainer';
import { WorldRelic } from './worlds/world';
import RelicMarker from './editor/RelicMarker';
import MegaGemMarker from './editor/entity-markers/MegaGemMarker';
import { TrapAnimated } from './TrapAnimated';
import CheckpointMarker from './editor/CheckpointMarker';

export type AddableToChunk = Phaser.GameObjects.Image | Phaser.GameObjects.Shape | Phaser.GameObjects.Container | EditorMarker;

export type Entity = EntityImage | EntityContainer;
export type Platform = PlatformImage | PlatformContainer;
export type Trap = TrapImage | TrapAnimated;

export interface ChunkBoundary {
  left: BoundsMarker | undefined;
  right: BoundsMarker | undefined;
}

export default class ChunkBase {
  protected scene: Phaser.Scene;
  protected x: number;
  protected y: number;
  protected boundary!: ChunkBoundary;
  protected foundFinishMarker: boolean;
  protected finishPointX: number;

  protected entityFactory: EntityFactory | undefined;

  protected editorMarkers: EditorMarker[];
  protected chunkObjects: AddableToChunk[];
  protected platformMarkers: EditorPlatformMarkerImage[];
  protected platforms: Platform[];
  protected trapMarkers: EditorTrapMarker[];
  protected traps: Trap[];
  protected entities: Entity[];
  protected checkPoints: Phaser.Math.Vector2[];

  constructor(scene: Phaser.Scene, x: number = 0, y: number = 0) {
    this.scene = scene;
    this.x = x;
    this.y = y;

    this.boundary = {
      left: undefined,
      right: undefined
    };

    this.foundFinishMarker = false;
    this.finishPointX = 0;

    this.editorMarkers = [];
    this.chunkObjects = [];
    this.platformMarkers = [];
    this.platforms = [];
    this.trapMarkers = [];
    this.traps = [];
    this.entities = [];

    this.checkPoints = [];
  }

  get AbsLeftSnapPointX(): number {
    return this.x;
  }

  get AbsLeftSnapPointY(): number {
    return this.y;
  }

  get AbsRightSnapPointX(): number {
    return this.x + (this.boundary.right?.x ?? 0);
  }

  get AbsRightSnapPointY(): number {
    return this.y + (this.boundary.right?.y ?? 0);
  }

  get FinishPointX(): number {
    return this.x + this.finishPointX;
  }

  get Checkpoints(): Phaser.Math.Vector2[] {
    return this.checkPoints;
  }

  public init(
    platformFactory: PlatformFactory,
    trapFactory: TrapFactory,
    entityFactory: EntityFactory,
    backgroundLayer: Phaser.GameObjects.Layer,
    foregroundLayer: Phaser.GameObjects.Layer,
    worldRelic?: WorldRelic
  ) {
    this.entityFactory = entityFactory;

    // let boundaryMarkers = ChunkBoundaries.getComponent(this);
    // this.leftBoundX = boundaryMarkers.leftBoundsMarker.x;
    // this.rightBoundX = boundaryMarkers.rightBoundsMarker.x;
    // this.rightBoundY = boundaryMarkers.rightBoundsMarker.y;

    // console.log(this.boundary, this.leftBoundX, this.rightBoundX);

    // Remove any space between the edge of the container and the left marker.
    this.x -= this.boundary.left?.x ?? 0;
    this.y -= this.boundary.left?.y ?? 0;

    // If this chunk doesn't have a finish marker then the finish point is the end of the chunk.
    if (!this.foundFinishMarker) this.finishPointX = this.boundary.right?.x ?? 0;

    // this.editorMarkers.forEach((marker) => {
    //   console.log(marker.constructor.name);
    //   marker.x -= this.boundary.left?.x ?? 0;
    //   marker.y -= this.boundary.left?.y ?? 0;
    // })

    this.spawnEditorMarkers(entityFactory, worldRelic);
    this.spawnPlatformMarkers(platformFactory);
    this.spawnTrapMarkers(trapFactory);

    this.platforms.forEach((platform) => {
      platform.x -= this.boundary.left?.x ?? 0;
      platform.y -= this.boundary.left?.y ?? 0;

      if (platform.IsMovingPlatform) {
        let simpleMover = SimpleMover.getComponent(platform);
        // simpleMover.startPoint.x -= this.boundary.left?.x ?? 0;
        // simpleMover.startPoint.y -= this.boundary.left?.y ?? 0;
        // simpleMover.endPoint.x -= this.boundary.left?.x ?? 0;
        // simpleMover.endPoint.y -= this.boundary.left?.y ?? 0;
      }

      if (platform.zIndex > 0 && platform.zIndex <= 1000) {
        platform.depth = platform.zIndex;
        backgroundLayer.add(platform);
      } else if (platform.zIndex > 1000 && platform.zIndex <= 2000) {
        platform.depth = platform.zIndex - 1000;
        foregroundLayer.add(platform);
      }

      platform.init();
    });

    this.traps.forEach((trap) => {
      trap.x -= this.boundary.left?.x ?? 0;
      trap.y -= this.boundary.left?.y ?? 0;

      if (trap.zIndex > 0 && trap.zIndex <= 1000) {
        trap.depth = trap.zIndex;
        backgroundLayer.add(trap as Phaser.GameObjects.GameObject);
      } else if (trap.zIndex > 1000 && trap.zIndex <= 2000) {
        trap.depth = trap.zIndex - 1000;
        foregroundLayer.add(trap as Phaser.GameObjects.GameObject);
      }

      // trap.init();
    });

    console.log(`Chunk Objects ${this.chunkObjects.length}`);
    this.chunkObjects.forEach((obj) => {
      obj.x -= this.boundary.left?.x ?? 0;
      obj.y -= this.boundary.left?.y ?? 0;

      if (obj instanceof DecorationImage) {
        if (obj.zIndex > 0 && obj.zIndex <= 1000) {
          obj.depth = obj.zIndex;
          backgroundLayer.add(obj);
        } else if (obj.zIndex > 1000 && obj.zIndex <= 2000) {
          obj.depth = obj.zIndex - 1000;
          foregroundLayer.add(obj);
        }
      }

      if (obj instanceof DecorationHelpSwitchDesk) {
        if (!this.scene.sys.game.device.os.desktop) {
          obj.visible = false;
        }
      }

      if (obj instanceof DecorationHelpJumpDesk) {
        if (!this.scene.sys.game.device.os.desktop) {
          obj.visible = false;
        }
      }
    });

    console.log('Found Checkpoints', this.checkPoints);
  }

  public awake() {}

  public reset() {
    // Respawn entities.
    for (const entity of this.entities) {
      if (entity instanceof EntitySmallGem || entity instanceof EntitySmashable) {
        entity.reset();
      }
    }
  }

  public shift(x: number, y: number) {
    let correctedX = x - (this.boundary.left?.x ?? 0);
    let correctedY = y - (this.boundary.left?.y ?? 0);

    let offsetX = this.x - correctedX;
    let offsetY = this.y - correctedY;

    this.x = correctedX;
    this.y = correctedY;

    this.platforms.forEach((platform) => {
      if (platform.IsMovingPlatform) {
        let simpleMover = SimpleMover.getComponent(platform);
        simpleMover.shift(-offsetX, -offsetY);
      } else {
        platform.x -= offsetX;
        platform.y -= offsetY;
      }
    });

    this.traps.forEach((traps) => {
      traps.x -= offsetX;
      traps.y -= offsetY;
    });

    this.chunkObjects.forEach((obj) => {
      obj.x -= offsetX;
      obj.y -= offsetY;
    });

    this.entities.forEach((entity) => {
      entity.x -= offsetX;
      entity.y -= offsetY;
    });
  }

  public recycle(x: number, y: number) {
    // this.shift(x, y);
    this.chunkObjects.forEach((object) => {
      if (object instanceof EditorMarker) return;

      object?.destroy?.();
    });

    this.chunkObjects = [];

    this.platforms.forEach((platform) => {
      if (platform.IsMovingPlatform) platform.destroy();
      else platform.recycle();

      // if (platform.IsMovingPlatform) {
      //   SimpleMover.clearComponent(platform);
      // }

      // platform.recycle();
    });

    this.platforms = [];

    this.traps.forEach((traps) => {
      traps.recycle();
    });

    this.traps = [];

    this.entities.forEach((entity) => {
      if (entity instanceof EntitySmallGem || entity instanceof EntitySmashable) {
        entity.recycle();
      } else {
        entity.destroy();
      }
    });

    this.entities = [];
  }

  // Clean up everything as the game is over.
  public destroy() {
    this.editorMarkers = [];

    this.chunkObjects.forEach((object) => {
      if (object instanceof EditorMarker) return;

      object?.destroy?.();
    });

    this.chunkObjects = [];

    this.platforms.forEach((platform) => {
      this.scene.tweens.killTweensOf(platform);
      platform.destroy();
    });

    this.platforms = [];

    this.traps.forEach((traps) => {
      traps.destroy();
    });

    this.traps = [];

    this.entities.forEach((entity) => {
      entity.destroy();
    });

    this.entities = [];
  }

  public removeEntity(entityToRemove: Entity) {
    let index = this.entities.indexOf(entityToRemove);
    if (index > -1) this.entities.splice(index, 1);
  }

  public removePlatform(platformToRemove: Platform) {
    let index = this.platforms.indexOf(platformToRemove);
    if (index > -1) this.platforms.splice(index, 1);
  }

  public removeTrap(trapToRemove: Trap) {
    let index = this.traps.indexOf(trapToRemove);
    if (index > -1) this.traps.splice(index, 1);
  }

  protected add(gameObject: AddableToChunk) {
    if (gameObject instanceof EditorMarker) {
      // console.log('Found a editor entity!', gameObject);

      if (gameObject instanceof BoundsMarker) {
        if (this.boundary.left == undefined) this.boundary.left = gameObject;
        else if (this.boundary.left.x > gameObject.x) {
          this.boundary.right = this.boundary.left;
          this.boundary.left = gameObject;
        } else if (this.boundary.right == undefined) this.boundary.right = gameObject;
        else console.log('We have too many boundaries', this.boundary, gameObject);
      } else if (gameObject instanceof FinishMarker) {
        this.foundFinishMarker = true;
        this.finishPointX = gameObject.x;
      } else {
        gameObject.x += this.x;
        gameObject.y += this.y;
      }

      if (gameObject instanceof EditorPlatformMarker) {
        this.platformMarkers.push(gameObject);
      } else if (gameObject instanceof EditorTrapMarker) {
        this.trapMarkers.push(gameObject);
      } else {
        this.editorMarkers.push(gameObject);
      }

      return;
    }

    gameObject.x += this.x;
    gameObject.y += this.y;

    if (gameObject instanceof PlatformImage) {
      this.platforms.push(gameObject);
      // this.scene.add.existing(gameObject);

      return;
    }

    this.chunkObjects.push(gameObject);

    // if (gameObject instanceof EntityImage) {
    //   this.scene.add.existing(gameObject);
    // }
  }

  protected spawnEditorMarkers(entityFactory: EntityFactory, worldRelic?: WorldRelic) {
    this.editorMarkers.forEach((entity) => {
      if (entity instanceof BoundsMarker) return;

      entity.x -= this.boundary.left?.x ?? 0;
      entity.y -= this.boundary.left?.y ?? 0;

      switch (entity.constructor) {
        case SmallGemMarker:
          let gem = entityFactory.makeSmallGem(this);
          gem.x = entity.x;
          gem.y = entity.y;
          this.scene.add.existing(gem);
          this.entities.push(gem);
          break;
        case MegaGemMarker:
          let megaGem = entityFactory.makeMegaGem(this);
          megaGem.x = entity.x;
          megaGem.y = entity.y;
          this.scene.add.existing(megaGem);
          this.entities.push(megaGem);
          break;
        case SmashableMarkerCrate:
          let simpleBreakableBlocker = SimpleBreakableBlocker.getComponent(entity);
          let crate = entityFactory.makeSmashableCrate(this, simpleBreakableBlocker.allowedCharacter);
          crate.x = entity.x;
          crate.y = entity.y;
          this.scene.add.existing(crate);
          this.entities.push(crate);
          break;
        case SmashableMarkerVine:
          let simpleBreakableBlocker1 = SimpleBreakableBlocker.getComponent(entity);
          let vine = entityFactory.makeSmashableVine(this, simpleBreakableBlocker1.allowedCharacter);
          vine.x = entity.x;
          vine.y = entity.y;
          this.scene.add.existing(vine);
          this.entities.push(vine);
          break;
        case SmashableMarkerRocks:
          let simpleBreakableBlocker2 = SimpleBreakableBlocker.getComponent(entity);
          let rocks = entityFactory.makeSmashableRocks(this, simpleBreakableBlocker2.allowedCharacter);
          rocks.x = entity.x;
          rocks.y = entity.y;
          this.scene.add.existing(rocks);
          this.entities.push(rocks);
          break;
        case RelicMarker:
          if (worldRelic) {
            let relic = entityFactory.makeRelic(worldRelic.relic, entity.x, entity.y);
            this.scene.add.existing(relic);
            this.entities.push(relic);
          }
          break;
        case CheckpointMarker:
          this.checkPoints.push(new Phaser.Math.Vector2(entity.x, entity.y));
          break;
        default:
          break;
      }
    });
  }

  protected spawnPlatformMarkers(platformFactory: PlatformFactory) {
    this.platformMarkers.forEach((platformMarker) => {
      let platform = platformFactory.makePlatform(platformMarker, this, platformMarker.x, platformMarker.y);
      if (platform) this.platforms.push(platform);
    });
  }

  protected spawnTrapMarkers(trapFactory: TrapFactory) {
    this.trapMarkers.forEach((trapMarker) => {
      switch (trapMarker.constructor) {
        case TrapMarkerSpikes:
          let trapSpike = trapFactory.makeTrapSpike(this, trapMarker.x, trapMarker.y);
          this.traps.push(trapSpike);
          break;
        case TrapMarkerCoconuts:
          let trapCoconut = trapFactory.makeTrapCoconut(this, trapMarker.x, trapMarker.y);
          this.traps.push(trapCoconut);
          break;
        case TrapMarkerTorch:
          let trapTorch = trapFactory.makeTrapTorch(this, trapMarker.x, trapMarker.y);
          this.traps.push(trapTorch);
          break;
        default:
          break;
      }
    });
  }
}
